import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import { Colors } from "../../utils/Colors";

const HISTORY_API =
  "http://127.0.0.1:8000/api/medcharts/medication-patient-history/?format=json";

const pageStyle = {
  minHeight: "100vh",
  background: Colors.bg.app,
  padding: "32px 18px",
};

const wrapperStyle = {
  maxWidth: "1380px",
  margin: "0 auto",
  background: Colors.bg.paper,
  borderRadius: "24px",
  boxShadow: Colors.shadow.brand,
  overflow: "hidden",
  border: `1px solid ${Colors.border.light}`,
};

const sectionCardStyle = {
  background: Colors.bg.paper,
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 4px 14px rgba(11,60,63,0.05)",
};

const tableWrapStyle = {
  overflowX: "auto",
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "16px",
  background: Colors.bg.paper,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "1500px",
};

const thStyle = {
  textAlign: "left",
  padding: "14px",
  background: Colors.bg.hover,
  color: Colors.text.header,
  fontSize: "13px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "14px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontSize: "14px",
  color: Colors.text.body,
  verticalAlign: "middle",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function getTypeLabel(type) {
  const map = {
    ANTIBIOTIC: "Antibiotic",
    ANALGESIC: "Analgesic",
    ANTIPYRETIC: "Antipyretic",
    ANTACID: "Antacid",
    ANTIHYPERTENSIVE: "Antihypertensive",
    ANTIDIABETIC: "Antidiabetic",
    INSULIN: "Insulin",
    IV_FLUID: "IV Fluid",
    VITAMIN: "Vitamin",
    OTHER: "Other",
  };
  return map[type] || type || "-";
}

function getRouteLabel(route) {
  const map = {
    ORAL: "Oral",
    IV: "IV",
    IM: "IM",
    SC: "SC",
    TOPICAL: "Topical",
    INHALATION: "Inhalation",
    RECTAL: "Rectal",
    NASAL: "Nasal",
    OTHER: "Other",
  };
  return map[route] || route || "-";
}

function getFrequencyLabel(frequency) {
  const map = {
    OD: "OD - Once Daily",
    BD: "BD - Twice Daily",
    TDS: "TDS - Three Times Daily",
    QID: "QID - Four Times Daily",
    SOS: "SOS - As Needed",
    STAT: "STAT - Immediately",
    Q4H: "Every 4 Hours",
    Q6H: "Every 6 Hours",
    Q8H: "Every 8 Hours",
    Q12H: "Every 12 Hours",
  };
  return map[frequency] || frequency || "-";
}

export default function MedicationPatientHistoryDetailsPage() {
  const location = useLocation();
  const { patientUhid } = useParams();

  const [historyRows, setHistoryRows] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(location.state?.patient || null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (patientUhid) {
      fetchPatientHistory(patientUhid);
    }
  }, [patientUhid]);

  const fetchPatientHistory = async (uhid) => {
    try {
      setLoadingHistory(true);
      setErrorMsg("");

      const url = `${HISTORY_API}&patient_uhid=${encodeURIComponent(uhid)}`;
      const res = await axios.get(url);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setHistoryRows(data);

      if (!selectedPatient && data.length > 0) {
        const first = data[0];
        setSelectedPatient({
          patient_uhid: first.patient_uhid,
          patient_name: first.patient_name,
          admission_number: first.admission_number,
          doctor_name: first.doctor_name,
          total_medications: data.length,
          last_medication_at: first.created_at,
        });
      }
    } catch (error) {
      console.error("Medication history fetch error:", error);
      setErrorMsg("Unable to load medication history.");
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={wrapperStyle}>
        <div
          style={{
            padding: "26px 30px 18px",
            background: Colors.bg.paper,
            borderBottom: `1px solid ${Colors.border.light}`,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 800,
              color: Colors.text.title,
              letterSpacing: "0.2px",
            }}
          >
            Medication Patient History
          </h2>

          <p
            style={{
              margin: "8px 0 0",
              color: Colors.text.muted,
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            This page shows full medication history for the selected patient.
          </p>

          <div
            style={{
              marginTop: "16px",
              height: "6px",
              width: "180px",
              borderRadius: "999px",
              background: Colors.brand.accentBar,
            }}
          />
        </div>

        <div style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {errorMsg}
            </div>
          )}

          {selectedPatient && (
            <div style={{ ...sectionCardStyle, marginBottom: "24px" }}>
              <div style={{ marginBottom: "18px" }}>
                <h3
                  style={{
                    margin: 0,
                    color: Colors.text.header,
                    fontSize: "18px",
                    fontWeight: 800,
                  }}
                >
                  Selected Patient Details
                </h3>

                <div
                  style={{
                    marginTop: "14px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "14px",
                  }}
                >
                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Patient UHID
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {selectedPatient.patient_uhid || "-"}
                    </div>
                  </div>

                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Patient Name
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {selectedPatient.patient_name || "-"}
                    </div>
                  </div>

                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Admission Number
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {selectedPatient.admission_number || "-"}
                    </div>
                  </div>

                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Doctor Name
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {selectedPatient.doctor_name || "-"}
                    </div>
                  </div>

                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Total Medicines
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {selectedPatient.total_medications || historyRows.length || 0}
                    </div>
                  </div>

                  <div
                    style={{
                      background: Colors.bg.hover,
                      border: `1px solid ${Colors.border.light}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: Colors.text.sub,
                        marginBottom: "4px",
                        fontWeight: 700,
                      }}
                    >
                      Last Medication Date
                    </div>
                    <div
                      style={{
                        color: Colors.text.title,
                        fontWeight: 800,
                        fontSize: "15px",
                      }}
                    >
                      {formatDateTime(selectedPatient.last_medication_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={sectionCardStyle}>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Admission No</th>
                    <th style={thStyle}>Patient UHID</th>
                    <th style={thStyle}>Patient Name</th>
                    <th style={thStyle}>Doctor Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Generic</th>
                    <th style={thStyle}>Brand</th>
                    <th style={thStyle}>Route</th>
                    <th style={thStyle}>Dose</th>
                    <th style={thStyle}>Frequency</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Side Effects</th>
                    <th style={thStyle}>Created At</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingHistory ? (
                    <tr>
                      <td colSpan="13" style={tdStyle}>
                        Loading history...
                      </td>
                    </tr>
                  ) : historyRows.length === 0 ? (
                    <tr>
                      <td colSpan="13" style={tdStyle}>
                        No medication history found.
                      </td>
                    </tr>
                  ) : (
                    historyRows.map((row) => (
                      <tr key={row.id}>
                        <td style={tdStyle}>{row.admission_number || "-"}</td>
                        <td style={tdStyle}>{row.patient_uhid || "-"}</td>
                        <td style={tdStyle}>{row.patient_name || "-"}</td>
                        <td style={tdStyle}>{row.doctor_name || "-"}</td>
                        <td style={tdStyle}>
                          {row.type_label || getTypeLabel(row.type)}
                        </td>
                        <td style={tdStyle}>{row.generic || "-"}</td>
                        <td style={tdStyle}>{row.brand || "-"}</td>
                        <td style={tdStyle}>
                          {row.route_label || getRouteLabel(row.route)}
                        </td>
                        <td style={tdStyle}>{row.dose || "-"}</td>
                        <td style={tdStyle}>
                          {row.frequency_label || getFrequencyLabel(row.frequency)}
                        </td>
                        <td style={tdStyle}>{row.duration || "-"}</td>
                        <td style={tdStyle}>{row.side || "-"}</td>
                        <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}